<template>
  <div
    ref="root"
    class="edge-popover"
    role="dialog"
    tabindex="-1"
    :aria-label="ariaLabel"
    @keydown.esc.stop.prevent="$emit('cancel')">

    <!-- Stage 1 — which traversal. Only what the graph offered out of this node. -->
    <template v-if="stage === Mode.Menu">
      <div class="edge-popover__title-row">
        <p class="edge-popover__heading">{{ copy.MenuHeading }}</p>
      </div>

      <div class="edge-popover__body">
        <p v-if="!offeredEdges.length" class="edge-popover__note">{{ copy.NoTraversalsNote }}</p>

        <button
          v-for="offered in offeredEdges"
          :key="edgeIdentity(offered)"
          type="button"
          class="edge-popover__option"
          @click="pickTraversal(offered)">
          <span class="edge-popover__option-title">
            {{ edgeLabel(offered) }}
            <span v-if="cardinalityBadge(offered)" class="edge-popover__badge">
              {{ cardinalityBadge(offered) }}
            </span>
            <span v-if="discriminatorBadge(offered)" class="edge-popover__badge">
              {{ discriminatorBadge(offered) }}
            </span>
          </span>
          <span class="edge-popover__option-detail">{{ offered.targetTable || humanize(offered.targetKind) }}</span>
        </button>
      </div>
    </template>

    <!-- Stage 2 — how it folds into the row. -->
    <template v-else>
      <div class="edge-popover__title-row">
        <button
          v-if="canGoBack"
          type="button"
          class="edge-popover__back"
          :aria-label="copy.MenuHeading"
          @click="backToMenu">
          <v-icon small>mdi-chevron-left</v-icon>
        </button>
        <p class="edge-popover__heading edge-popover__heading--tight">{{ configureTitle }}</p>
      </div>

      <div class="edge-popover__body">
        <div v-if="targetIdRequired" class="edge-popover__group">
          <v-autocomplete
            v-model="targetId"
            :items="registerOptions"
            :loading="registerOptionsLoading"
            item-text="name"
            item-value="id"
            :label="copy.RegisterTargetLabel"
            :no-data-text="copy.NoRegisterTargets"
            :aria-required="targetIdRequired ? 'true' : null"
            class="edge-popover__register-select"
            dense
            outlined
            clearable
            attach
            hide-details
            @change="logChange('targetId', targetId)" />
          <p class="edge-popover__note">{{ copy.RegisterTargetHint }}</p>
        </div>

        <!-- TPH discriminator: the target table holds every tag kind. One kind, or any of them. -->
        <div v-if="needsDiscriminator" class="edge-popover__group">
          <label :for="discriminatorSelectId" class="edge-popover__group-label">{{ copy.DiscriminatorHeading }}</label>
          <select
            :id="discriminatorSelectId"
            v-model="discriminator"
            class="edge-popover__control"
            :aria-required="discriminatorRequired ? 'true' : null"
            @change="selectDiscriminator(discriminator)">
            <!-- The null value is "any type" when the graph allows it, and a prompt that cannot be chosen when it does not. -->
            <option v-if="discriminatorRequired" :value="null" disabled>{{ copy.NeedsDiscriminator }}</option>
            <option v-else :value="null">All Tag Types (Any)</option>
            <option v-for="option in discriminators" :key="option" :value="option">{{ option }}</option>
          </select>
        </div>

        <!-- Strategy: rendered from the graph's own list, never a fixed set of options. -->
        <div v-if="strategies.length" class="edge-popover__group">
          <label :for="strategySelectId" class="edge-popover__group-label">{{ strategyHeading }}</label>
          <select
            :id="strategySelectId"
            v-model="strategyKind"
            class="edge-popover__control"
            :aria-describedby="selectedStrategyDescription ? strategyDescriptionId : null"
            @change="selectStrategy(strategyKind)">
            <!-- Like an unchecked radio group: the prompt exists only until a strategy is chosen. -->
            <option v-if="!strategyKind" :value="null" disabled>{{ copy.NeedsStrategy }}</option>
            <option v-for="kind in strategies" :key="kind" :value="kind">{{ strategyTitle(kind) }}</option>
          </select>
          <p v-if="selectedStrategyDescription" :id="strategyDescriptionId" class="edge-popover__note">
            {{ selectedStrategyDescription }}
          </p>
        </div>

        <!-- `latest` ranks by a column; date-like columns are offered first. -->
        <div v-if="needsOrderBy" class="edge-popover__group">
          <span class="edge-popover__group-label">{{ copy.OrderByLabel }}</span>
          <!-- The tag table exposes 284 columns; grouping puts the nine dates within reach. -->
          <select
            v-if="orderByOptions.length"
            v-model="orderBy"
            class="edge-popover__control"
            :aria-label="copy.OrderByLabel"
            @change="logChange('orderBy', orderBy)">
            <optgroup v-for="group in orderByGroups" :key="group.label" :label="group.label">
              <option v-for="column in group.columns" :key="column.name" :value="column.name">
                {{ column.name }}
              </option>
            </optgroup>
          </select>
          <p class="edge-popover__note">
            {{ orderByOptions.length ? copy.OrderByHint : copy.NoColumnsToRankBy }}
          </p>
        </div>

        <!-- `list` aggregates a column; default is Name. -->
        <div v-if="isListSelected" class="edge-popover__group">
          <span class="edge-popover__group-label">{{ copy.ListPropertyLabel }}</span>
          <select
            v-if="orderByOptions.length"
            v-model="listColumn"
            class="edge-popover__control"
            :aria-label="copy.ListPropertyLabel"
            @change="logChange('listColumn', listColumn)">
            <optgroup v-for="group in orderByGroups" :key="group.label" :label="group.label">
              <option v-for="column in group.columns" :key="column.name" :value="column.name">
                {{ column.name }}
              </option>
            </optgroup>
          </select>
          <p class="edge-popover__note">
            {{ copy.ListPropertyHint }}
          </p>
        </div>

        <!-- count / exists / list ARE a column, so they carry their own name. -->
        <div v-if="isScalarSelected" class="edge-popover__group">
          <span class="edge-popover__group-label">{{ copy.AliasLabel }}</span>
          <input
            v-model="alias"
            type="text"
            class="edge-popover__control"
            :aria-label="copy.AliasLabel"
            :placeholder="aliasPlaceholder"
            @change="logChange('alias', alias)" />
          <p v-if="derivedAliasDiffers" class="edge-popover__note">
            {{ copy.AliasDerivedPrefix }} <strong>{{ derivedAlias }}</strong>
          </p>
          <p class="edge-popover__note">{{ copy.AliasHint }}</p>
        </div>

        <div v-if="isFanOut(strategyKind)" class="edge-popover__warning">
          <v-icon small class="edge-popover__warn-icon">mdi-alert-outline</v-icon>
          <span>{{ copy.FanOutBannerBody }}</span>
        </div>
      </div>

      <p v-if="blockedReason" class="edge-popover__blocked">{{ blockedReason }}</p>

      <div class="edge-popover__footer">
        <button type="button" class="edge-popover__text-button" @click="$emit('cancel')">
          {{ copy.Cancel }}
        </button>
        <button
          type="button"
          class="edge-popover__submit"
          :disabled="!!blockedReason"
          @click="submit">
          {{ isEditing ? copy.Save : copy.Confirm }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
  import { log } from '@/utils/debugLogger';
  import {
    CompositeStrategy,
    DefaultListColumn,
    isSafeSqlName,
    isScalarStrategy,
    toSafeSqlName,
  } from '../../model/definitionVocabulary';
  import {
    EdgePopoverMode,
    RelationsCopy,
    humanizeWireWord,
    strategyCopyOf,
  } from './relationsCopy';
  import {
    GraphCardinality,
    cardinalityOf,
    defaultOrderByOf,
    discriminatorsOf,
    findGraphEdge,
    graphEdgeIdentity,
    graphEdgeLabel,
    graphEdgesFrom,
    isDateLikeColumn,
    isOneClickTraversal,
    orderByOptionsOf,
    requiresDiscriminator,
    requiresTargetId,
    strategyOptionsOf,
  } from './relationsGraph';

  function capitalizeFirst(text) {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  const IdStem = 'edge-popover';
  let instanceCount = 0;

  /**
   * A prefix for this instance's `label for` / `aria-describedby` ids. The step opens one popover
   * at a time, but ids are document-wide, so each instance still mints its own.
   */
  function nextIdPrefix() {
    instanceCount += 1;
    return `${IdStem}-${instanceCount}`;
  }

  /**
   * The one place a relation is configured, for both views and for both jobs: adding a traversal
   * the graph offers, and re-deciding how an existing one folds in.
   *
   * It renders as a plain absolutely-positioned panel rather than a `v-menu` on purpose. A menu
   * teleports its content to the application root, which puts it in a different stacking context
   * from the wizard dialog it belongs to and makes the popover a specificity fight; the panel
   * stays inside the step, where it is positioned by the step against its own box.
   *
   * It decides nothing about the definition: it emits what the author chose, and the step calls
   * the model. What it DOES own is refusing to emit something the server would reject — a missing
   * tag kind, a `latest` with nothing to rank by — because those two rules live in the graph
   * payload, which the definition model never sees.
   */
  export default {
    name: 'EdgePopover',

    props: {
      /** Authoring graph payload, or null when this definition has none yet. */
      graph: { type: Object, default: null },
      /** Node the relation hangs off — `root` or an edge id. Decides whether fan-out is offered. */
      parentNodeId: { type: String, required: true },
      /** Current local definition state, read only to compare fan-out ancestry. */
      relationState: { type: Object, required: true },
      /** Edge being reconfigured, or the parent-only shape of the proposed edge. */
      candidateEdge: { type: Object, required: true },
      /** Node kind of that parent, which is what the graph keys its traversals by. */
      parentKind: { type: String, default: null },
      /** Label of the node being added to, for the panel's own heading. */
      parentLabel: { type: String, default: '' },
      /** The stored edge when reconfiguring one; null when adding. */
      existingEdge: { type: Object, default: null },
      registerOptions: { type: Array, default: () => [] },
      registerOptionsLoading: { type: Boolean, default: false },
    },

    data() {
      return {
        copy: RelationsCopy,
        Mode: EdgePopoverMode,
        idPrefix: nextIdPrefix(),
        stage: EdgePopoverMode.Menu,
        graphEdge: null,
        targetId: null,
        discriminator: null,
        strategyKind: null,
        orderBy: null,
        listColumn: DefaultListColumn,
        alias: '',
      };
    },

    computed: {
      isEditing() {
        return !!this.existingEdge;
      },

      canGoBack() {
        return !this.isEditing && this.offeredEdges.length > 1;
      },

      offeredEdges() {
        return graphEdgesFrom(this.graph, this.parentKind);
      },

      targetKind() {
        return this.graphEdge ? this.graphEdge.targetKind : null;
      },

      targetIdRequired() {
        return requiresTargetId(this.graphEdge);
      },

      targetIdAvailable() {
        return !!this.targetId && this.registerOptions.some((option) => option.id === this.targetId);
      },

      discriminators() {
        return discriminatorsOf(this.graphEdge);
      },

      needsDiscriminator() {
        return this.discriminators.length > 0;
      },

      discriminatorRequired() {
        return requiresDiscriminator(this.graphEdge);
      },

      discriminatorSelectId() {
        return `${this.idPrefix}-discriminator`;
      },

      strategySelectId() {
        return `${this.idPrefix}-strategy`;
      },

      strategyDescriptionId() {
        return `${this.strategySelectId}-description`;
      },

      strategies() {
        return strategyOptionsOf(this.graphEdge, this.relationState, this.candidateEdge);
      },

      strategyHeading() {
        return cardinalityOf(this.graphEdge) === GraphCardinality.ToOne
          ? this.copy.StrategyHeadingToOne
          : this.copy.StrategyHeading;
      },

      selectedStrategyDescription() {
        return this.strategyKind ? this.strategyDescription(this.strategyKind) : '';
      },

      needsOrderBy() {
        return this.strategyKind === CompositeStrategy.Latest;
      },

      isListSelected() {
        return this.strategyKind === CompositeStrategy.List;
      },

      isScalarSelected() {
        return isScalarStrategy(this.strategyKind);
      },

      orderByOptions() {
        return orderByOptionsOf(this.graph, this.targetKind);
      },

      /**
       * The ordering columns in two named groups. Every column the graph offered stays reachable —
       * the server asks only for a safe name, and ranking by a revision or sequence is legitimate —
       * but the dates are lifted out of a list that is 284 long on a tag relation.
       */
      orderByGroups() {
        const dated = this.orderByOptions.filter(isDateLikeColumn);
        const rest = this.orderByOptions.filter((column) => !isDateLikeColumn(column));

        return [
          { label: this.copy.DateColumnsGroup, columns: dated },
          { label: this.copy.OtherColumnsGroup, columns: rest },
        ].filter((group) => group.columns.length);
      },

      /**
       * The name the column will actually take. The author types intent — "Linked Tags" — and the
       * compiler needs an identifier, so the wizard derives one rather than refusing the input and
       * making them guess the allow-list. Empty stays empty: an unnamed scalar edge is blocked
       * below, not silently named.
       */
      derivedAlias() {
        const typed = (this.alias || '').trim();
        return typed ? toSafeSqlName(typed, '') : '';
      },

      /** True when the derived name is not what was typed, so the author is told before saving. */
      derivedAliasDiffers() {
        const typed = (this.alias || '').trim();
        return !!typed && this.derivedAlias !== typed;
      },

      aliasPlaceholder() {
        if (!this.targetKind || !this.strategyKind) return '';
        const base = this.discriminator || capitalizeFirst(this.targetKind);
        if (this.isListSelected) {
          return `${base}List`;
        }
        const kind = this.strategyKind;
        return `${base}${capitalizeFirst(kind)}`;
      },

      configureTitle() {
        const target = this.targetKind ? this.humanize(this.targetKind) : '';
        if (this.discriminator) return `${target}: ${this.discriminator}`;
        if (target && this.targetIdRequired) return target;
        if (target && !this.discriminatorRequired) return `${target} (All Types)`;
        return target || this.parentLabel;
      },

      ariaLabel() {
        return this.stage === EdgePopoverMode.Menu ? this.copy.MenuHeading : this.copy.ConfigureRelation;
      },

      /**
       * Why the author cannot confirm yet, or null. These are the graph's rules, checked here
       * because the definition model has no graph to check them against: an ordering column
       * is accepted by every mutator and refused by the server.
       */
      blockedReason() {
        if (this.targetIdRequired && !this.targetIdAvailable) return this.copy.NeedsRegisterTarget;
        if (!this.strategyKind) return this.copy.NeedsStrategy;
        if (this.discriminatorRequired && !this.discriminator) return this.copy.NeedsDiscriminator;
        if (this.needsOrderBy && !this.orderBy) return this.copy.NeedsOrderBy;

        // A scalar edge IS a column, so an unnamed or underivable one cannot be saved. Caught here
        // rather than at the server: `isSafeSqlName` mirrors `DataSourceSqlText.IsSafeName`, so what
        // this refuses is exactly what the compiler would have refused one screen later.
        if (this.isScalarSelected && !isSafeSqlName(this.derivedAlias)) {
          return this.copy.NeedsAlias;
        }

        return null;
      },
    },

    /** State derived from props belongs here, before the first render draws it. */
    created() {
      if (this.isEditing) this.startFromExistingEdge();
    },

    mounted() {
      if (this.$refs.root) this.$refs.root.focus();
    },

    methods: {
      humanize: humanizeWireWord,
      edgeIdentity: graphEdgeIdentity,
      edgeLabel: graphEdgeLabel,

      isFanOut(kind) {
        return kind === CompositeStrategy.FanOut;
      },

      strategyTitle(kind) {
        return strategyCopyOf(kind).title;
      },

      strategyDescription(kind) {
        return strategyCopyOf(kind).description;
      },

      cardinalityBadge(offered) {
        const cardinality = cardinalityOf(offered);
        if (cardinality === GraphCardinality.ToMany) return this.copy.ToManyBadge;
        if (cardinality === GraphCardinality.ToOne) return this.copy.ToOneBadge;
        return null;
      },

      discriminatorBadge(offered) {
        const count = discriminatorsOf(offered).length;
        return count === 1 ? '1 Type' : `${count} Types`;
      },

      /** Reconfiguring: open straight on the traversal the stored edge already walks. */
      startFromExistingEdge() {
        const edge = this.existingEdge;
        this.graphEdge = findGraphEdge(
          this.graph,
          this.parentKind,
          edge.target,
          edge.traversalKey,
        );
        this.targetId = edge.targetId || null;
        this.discriminator = edge.discriminator || null;
        this.strategyKind = edge.strategy ? edge.strategy.kind : null;
        this.orderBy = (edge.strategy && edge.strategy.orderBy) || null;
        this.listColumn = (edge.strategy && edge.strategy.column) || DefaultListColumn;
        this.alias = edge.alias || '';
        this.stage = EdgePopoverMode.Configure;

        log('[EdgePopover] configuring an existing relation', {
          edgeId: edge.id,
          target: edge.target,
          strategy: this.strategyKind,
        });
      },

      /**
       * A traversal with nothing to decide is added on the click that picked it — there is no
       * second screen to show. Anything that needs a tag kind, an ordering column, a name, or that
       * changes what a row means goes to the configure panel so the author sees the decision.
       */
      pickTraversal(offered) {
        log('[EdgePopover] traversal picked', {
          from: this.parentNodeId,
          target: offered.targetKind,
          oneClick: isOneClickTraversal(offered, this.relationState, this.candidateEdge),
        });

        this.graphEdge = offered;
        this.targetId = null;
        this.discriminator = null;
        this.listColumn = DefaultListColumn;
        this.alias = '';

        if (isOneClickTraversal(offered, this.relationState, this.candidateEdge)) {
          this.strategyKind = strategyOptionsOf(
            offered,
            this.relationState,
            this.candidateEdge,
          )[0];
          this.orderBy = null;
          this.submit();
          return;
        }

        const strategies = strategyOptionsOf(offered, this.relationState, this.candidateEdge);
        this.strategyKind = strategies.length === 1 ? strategies[0] : null;
        this.orderBy = this.strategyKind === CompositeStrategy.Latest
          ? defaultOrderByOf(this.graph, offered.targetKind)
          : null;
        this.stage = EdgePopoverMode.Configure;
      },

      backToMenu() {
        log('[EdgePopover] back to the traversal list', { from: this.parentNodeId });
        this.stage = EdgePopoverMode.Menu;
        this.graphEdge = null;
        this.targetId = null;
        this.strategyKind = null;
      },

      selectDiscriminator(option) {
        log('[EdgePopover] tag kind picked', { from: this.parentNodeId, discriminator: option });
        this.discriminator = option || null;
      },

      /** Switching to `latest` lands on a column to rank by, so the step is never blocked silently. */
      selectStrategy(kind) {
        log('[EdgePopover] strategy picked', { from: this.parentNodeId, strategy: kind });
        this.strategyKind = kind;

        if (kind === CompositeStrategy.Latest && !this.orderBy) {
          this.orderBy = defaultOrderByOf(this.graph, this.targetKind);
        }
        if (kind === CompositeStrategy.List && !this.listColumn) {
          this.listColumn = DefaultListColumn;
        }
      },

      logChange(field, value) {
        log('[EdgePopover] relation detail changed', { from: this.parentNodeId, field, value });
      },

      submit() {
        if (this.blockedReason) return;

        const payload = {
          target: this.targetKind,
          discriminator: this.discriminator || null,
          strategy: {
            kind: this.strategyKind,
            orderBy: this.needsOrderBy ? this.orderBy : null,
            column: this.isListSelected ? (this.listColumn || DefaultListColumn) : null,
          },
          alias: this.isScalarSelected ? this.derivedAlias : null,
        };
        if (this.graphEdge.traversalKey) payload.traversalKey = this.graphEdge.traversalKey;
        if (this.graphEdge.label) payload.traversalLabel = this.graphEdge.label;
        if (this.targetIdRequired) payload.targetId = this.targetId;

        log('[EdgePopover] relation submitted', {
          from: this.parentNodeId,
          editing: this.isEditing,
          target: payload.target,
          targetId: payload.targetId || null,
          discriminator: payload.discriminator,
          strategy: payload.strategy.kind,
        });

        this.$emit('submit', payload);
      },
    },
  };
</script>

<style scoped>
  .edge-popover {
    display: flex;
    flex-direction: column;
    width: 360px;
    max-width: calc(100vw - 32px);
    max-height: 480px;
    padding: 0;
    background: var(--rel-surface, #fcfdfc);
    border: 1px solid var(--rel-border-strong, #c7cdcb);
    border-radius: 12px;
    box-shadow: 0 10px 32px rgba(2, 26, 26, 0.18);
    outline: none;
    overflow: hidden;
  }

  .edge-popover__title-row {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 14px 10px;
    border-bottom: 1px solid var(--rel-border, #dfe3e1);
    background: var(--rel-surface, #fcfdfc);
  }

  .edge-popover__heading {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: var(--rel-ink, #182420);
  }

  .edge-popover__heading--tight {
    font-size: 12.5px;
  }

  .edge-popover__back {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin-right: -2px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: var(--rel-ink-muted, #5c6b66);
    cursor: pointer;
  }

  .edge-popover__back:hover {
    background: var(--rel-primary-soft, rgba(3, 98, 76, 0.08));
    color: var(--rel-primary, #03624c);
  }

  .edge-popover__option {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    width: 100%;
    margin-bottom: 6px;
    padding: 9px 11px;
    border: 1px solid var(--rel-border, #dfe3e1);
    border-radius: 9px;
    background: var(--rel-surface, #fcfdfc);
    text-align: left;
    cursor: pointer;
  }

  .edge-popover__option:hover {
    border-color: var(--rel-primary, #03624c);
    background: var(--rel-primary-soft, rgba(3, 98, 76, 0.08));
  }

  .edge-popover__option-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 700;
    color: var(--rel-ink, #182420);
  }

  .edge-popover__option-detail {
    font-size: 11px;
    color: var(--rel-ink-muted, #5c6b66);
  }

  .edge-popover__badge {
    padding: 1px 6px;
    border: 1px solid var(--rel-border-strong, #c7cdcb);
    border-radius: 4px;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: var(--rel-ink-muted, #5c6b66);
    white-space: nowrap;
  }

  .edge-popover__body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 12px 14px 8px;
  }

  .edge-popover__group {
    margin-bottom: 12px;
  }

  .edge-popover__group-label {
    display: block;
    margin-bottom: 6px;
    font-size: 11.5px;
    font-weight: 600;
    color: var(--rel-ink-muted, #5c6b66);
  }

  .edge-popover__control {
    width: 100%;
    height: 32px;
    padding: 0 8px;
    border: 1px solid var(--rel-border-strong, #c7cdcb);
    border-radius: 6px;
    background: var(--rel-surface, #fcfdfc);
    color: var(--rel-ink, #182420);
    font-size: 12.5px;
  }

  .edge-popover__control:focus-visible {
    border-color: var(--rel-primary, #03624c);
    outline: 2px solid rgba(3, 98, 76, 0.22);
    outline-offset: 1px;
  }

  .edge-popover__locked {
    margin: 0;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--rel-ink, #182420);
  }

  .edge-popover__note {
    margin: 5px 0 0;
    padding: 0 10px 4px 0;
    font-size: 11.5px;
    color: var(--rel-ink-muted, #5c6b66);
  }

  .edge-popover__warning,
  .edge-popover__blocked {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 10px;
    border: 1px solid var(--rel-warn-line, #c77d00);
    border-radius: 8px;
    font-size: 11.5px;
    color: var(--rel-warn-ink, #8a5600);
  }

  .edge-popover__blocked {
    flex-shrink: 0;
    margin: 4px 14px 6px;
  }

  .edge-popover__warn-icon {
    color: var(--rel-warn-ink, #8a5600);
  }

  .edge-popover__footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    padding: 8px 14px 10px;
    border-top: 1px solid var(--rel-border, #dfe3e1);
    background: var(--rel-surface, #fcfdfc);
    flex-shrink: 0;
  }

  .edge-popover__text-button {
    height: 32px;
    padding: 0 10px;
    border-radius: 6px;
    font-size: 12.5px;
    color: var(--rel-ink-muted, #5c6b66);
  }

  .edge-popover__submit {
    height: 32px;
    padding: 0 14px;
    border-radius: 8px;
    background: var(--rel-primary, #03624c);
    color: #fff;
    font-size: 12.5px;
    font-weight: 600;
  }

  .edge-popover__submit:disabled {
    background: #a7b5b1;
    cursor: not-allowed;
  }
</style>
